/* eslint-disable bsky-internal/avoid-unwrapped-text */
import './RemoveUserForm.css'

import {useState} from 'react'
import {View} from 'react-native'
import {type ComAtprotoServerDescribeServer} from '@atproto/api'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'
import {useQuery} from '@tanstack/react-query'

import {useAgent} from '#/state/session'
import {atoms as a, useIsMobileWeb, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button.tsx'
import {FormError} from '#/components/forms/FormError'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'
import {type QrSessionInitResult} from '../SignUpWizard/useQrSession'
import {CombinedFlow} from './CombinedFlow.web'
import {CombinedFlowMobile} from './CombinedFlowMobile.web'
import {EmailFlow} from './EmailFlow.web'
import {FormContainer} from './FormContainer'
import {MethodSelection} from './MethodSelection.web'
import {WidFlow} from './WidFlow.web'
import {WidFlowMobile} from './WidFlowMobile.web'

type ServiceDescription = ComAtprotoServerDescribeServer.OutputSchema

/** Extracts WID session fields if all required ones are present */
function extractWidData(
  data:
    | {sessionId?: string; sessionToken?: string; qrCodeUrl?: string}
    | undefined,
): QrSessionInitResult | null {
  if (data?.sessionId && data?.sessionToken) {
    return {
      sessionId: data.sessionId,
      sessionToken: data.sessionToken,
      qrCodeUrl: data.qrCodeUrl,
    }
  }
  return null
}

export const RemoveUserForm = ({
  error,
  serviceUrl: _serviceUrl,
  onPressBack,
  onAttemptSuccess,
}: {
  error: string
  serviceUrl: string
  serviceDescription: ServiceDescription | undefined
  initialHandle: string
  setError: (v: string) => void
  setServiceUrl: (v: string) => void
  onPressRetryConnect: () => void
  onPressBack: () => void
  onPressForgotPassword: () => void
  onAttemptSuccess: () => void
  onAttemptFailed: () => void
}) => {
  const {_} = useLingui()
  const t = useTheme()
  const agent = useAgent()
  const isMobileWeb = useIsMobileWeb()
  // selectedMethod is only used in the wid+email fallback path
  const [selectedMethod, setSelectedMethod] = useState<'wid' | 'email' | null>(
    null,
  )

  const methodQuery = useQuery({
    queryKey: ['remove-user-method'],
    queryFn: ({signal}) => agent.account.requestDeleteAccount({}, {signal}),
  })

  if (methodQuery.isPending) {
    return (
      <FormContainer
        testID="loginForm"
        style={{backgroundColor: 'transparent'}}>
        <View style={[a.align_center, a.gap_md, a.py_xl]}>
          <Loader fill={t.palette.white} size="lg" />
        </View>
        <View style={[a.flex_row, a.align_center, a.pt_md, a.self_center]}>
          <Button
            label={_(msg`Retry`)}
            variant="solid"
            color="secondary"
            size="small"
            onPress={onPressBack}>
            <ButtonText>
              <Trans>Back</Trans>
            </ButtonText>
          </Button>
        </View>
      </FormContainer>
    )
  }

  if (methodQuery.isError) {
    return (
      <FormContainer
        testID="loginForm"
        style={{backgroundColor: 'transparent'}}>
        <View style={[a.align_center, a.gap_md, a.py_xl]}>
          <Text style={[a.text_md, {color: t.palette.negative_500}]}>
            {methodQuery.error?.message}
          </Text>
          <Button
            label={_(msg`Retry`)}
            variant="solid"
            color="secondary"
            size="small"
            onPress={() => void methodQuery.refetch()}>
            <ButtonText>
              <Trans>Retry</Trans>
            </ButtonText>
          </Button>
        </View>
        <FormError error={error} />
        <View style={[a.flex_row, a.align_center, a.pt_md, a.self_center]}>
          <Button
            label={_(msg`Retry`)}
            variant="solid"
            color="secondary"
            size="small"
            onPress={onPressBack}>
            <ButtonText>
              <Trans>Back</Trans>
            </ButtonText>
          </Button>
        </View>
      </FormContainer>
    )
  }

  const {method} = methodQuery.data
  const widData = extractWidData(methodQuery.data)

  // wid — direct data available: skip second request and show QR immediately
  if (method === 'wid' && widData) {
    return isMobileWeb ? (
      <WidFlowMobile
        initialData={widData}
        error={error}
        onPressBack={onPressBack}
        onAttemptSuccess={onAttemptSuccess}
      />
    ) : (
      <WidFlow
        initialData={widData}
        error={error}
        onPressBack={onPressBack}
        onAttemptSuccess={onAttemptSuccess}
      />
    )
  }

  // wid — fallback: no session data from first request, make the second request
  if (method === 'wid') {
    return isMobileWeb ? (
      <WidFlowMobile
        error={error}
        onPressBack={onPressBack}
        onAttemptSuccess={onAttemptSuccess}
      />
    ) : (
      <WidFlow
        error={error}
        onPressBack={onPressBack}
        onAttemptSuccess={onAttemptSuccess}
      />
    )
  }

  // email — requestDeleteAccount already sent the email; skip to code entry
  if (method === 'email') {
    return (
      <EmailFlow error={error} onPressBack={onPressBack} emailAlreadySent />
    )
  }

  // wid+email — direct data: show QR/deeplink and email form simultaneously
  if (method === 'wid+email' && widData) {
    return isMobileWeb ? (
      <CombinedFlowMobile
        widInitialData={widData}
        error={error}
        onPressBack={onPressBack}
      />
    ) : (
      <CombinedFlow
        widInitialData={widData}
        error={error}
        onPressBack={onPressBack}
      />
    )
  }

  // wid+email fallback: session data missing — let user choose method
  if (method === 'wid+email') {
    if (!selectedMethod) {
      return (
        <MethodSelection
          error={error}
          onSelectMethod={setSelectedMethod}
          onPressBack={onPressBack}
        />
      )
    }
    if (selectedMethod === 'wid') {
      return isMobileWeb ? (
        <WidFlowMobile
          error={error}
          onPressBack={onPressBack}
          onAttemptSuccess={onAttemptSuccess}
        />
      ) : (
        <WidFlow
          error={error}
          onPressBack={onPressBack}
          onAttemptSuccess={onAttemptSuccess}
        />
      )
    }
    return <EmailFlow error={error} onPressBack={onPressBack} />
  }

  return (
    <FormContainer testID="loginForm" style={{backgroundColor: 'transparent'}}>
      <View style={[a.align_center, a.gap_md, a.py_xl]}>
        <Loader fill={t.palette.white} size="lg" />
      </View>
    </FormContainer>
  )
}
